import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/context/AuthContext';
import { 
  User, Mail, Phone, MapPin, Truck, Lock, Save, Plus, 
  UserPlus, Users, Pencil, Trash2, AlertCircle, Car, Building, 
  CreditCard, FileText, Info
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DriverProfile() {
  const { user, updateProfile, changePassword } = useAuth();
  const [fullProfile, setFullProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    contact_number: '',
    address: '',
    vehicle_details: ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  // Employee state
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    employee_type: 'driver',
    full_name: '',
    id_number: '',
    contact_number: '',
    address: '',
    vehicle_registration: '',
    license_number: '',
    pdp_number: '',
    notes: ''
  });
  const [employeeLoading, setEmployeeLoading] = useState(false);

  useEffect(() => {
    fetchFullProfile();
    fetchEmployees();
  }, []);

  const fetchFullProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile/details`);
      setFullProfile(response.data);
      // Update editable fields
      setProfileData({
        full_name: response.data.full_name || '',
        contact_number: response.data.contact_number || '',
        address: response.data.address || '',
        vehicle_details: response.data.vehicle_details || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to load employees:', error);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    
    try {
      await updateProfile(profileData);
      toast.success('Profile updated successfully. Admin has been notified.');
      fetchFullProfile();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change password'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const resetEmployeeForm = () => {
    setEmployeeForm({
      employee_type: 'driver',
      full_name: '',
      id_number: '',
      contact_number: '',
      address: '',
      vehicle_registration: '',
      license_number: '',
      pdp_number: '',
      notes: ''
    });
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    setEmployeeLoading(true);
    
    try {
      await axios.post(`${API_URL}/profile/employees`, employeeForm);
      toast.success(`${employeeForm.employee_type === 'driver' ? 'Driver' : 'Assistant'} added successfully. Admin has been notified.`);
      setShowAddDialog(false);
      resetEmployeeForm();
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add employee'));
    } finally {
      setEmployeeLoading(false);
    }
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    
    // Check if this is an embedded employee (from profile data)
    if (selectedEmployee?.source === 'profile' || selectedEmployee?.source === 'vehicle') {
      toast.error('This employee is from your registration data. Please contact admin to make changes.');
      setShowEditDialog(false);
      return;
    }
    
    setEmployeeLoading(true);
    
    try {
      await axios.put(`${API_URL}/profile/employees/${selectedEmployee.id}`, employeeForm);
      toast.success('Employee updated successfully. Admin has been notified.');
      setShowEditDialog(false);
      resetEmployeeForm();
      setSelectedEmployee(null);
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update employee'));
    } finally {
      setEmployeeLoading(false);
    }
  };

  const handleDeleteEmployee = async (employee) => {
    // Check if this is an embedded employee
    if (employee.source === 'profile' || employee.source === 'vehicle') {
      toast.error('This employee is from your registration data. Please contact admin to make changes.');
      return;
    }
    
    if (!confirm(`Request to remove ${employee.full_name}? Admin will be notified and must approve the deletion.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/profile/employees/${employee.id}`);
      toast.success('Deletion request sent to admin for approval.');
      fetchEmployees();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to request deletion'));
    }
  };

  const openEditDialog = (employee) => {
    if (employee.source === 'profile' || employee.source === 'vehicle') {
      toast.info('This employee is from your registration data. Contact admin to edit.');
      return;
    }
    
    setSelectedEmployee(employee);
    setEmployeeForm({
      employee_type: employee.employee_type || 'driver',
      full_name: employee.full_name || '',
      id_number: employee.id_number || '',
      contact_number: employee.contact_number || '',
      address: employee.address || '',
      vehicle_registration: employee.vehicle_registration || '',
      license_number: employee.license_number || '',
      pdp_number: employee.pdp_number || '',
      notes: employee.notes || ''
    });
    setShowEditDialog(true);
  };

  const getStatusBadge = (status, source) => {
    if (source === 'profile' || source === 'vehicle') {
      return <Badge className="bg-blue-100 text-blue-700">From Registration</Badge>;
    }
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'pending_deletion':
        return <Badge className="bg-red-100 text-red-700">Pending Deletion</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Helper for displaying profile info
  const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-start gap-2">
      {Icon && <Icon className="h-4 w-4 mt-0.5 text-muted-foreground" />}
      <div>
        <p className="text-xs text-muted-foreground uppercase">{label}</p>
        <p className="font-medium">{value || '-'}</p>
      </div>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="driver-profile">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
          <p className="text-muted-foreground mt-1">Manage your account, details, and employees</p>
        </div>

        {/* Owner Information (Read-only from admin data) */}
        {fullProfile && (fullProfile.company_trading_as || fullProfile.bank_name || fullProfile.driver_name) && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <Info className="h-5 w-5" />
                Your Registered Details
              </CardTitle>
              <CardDescription>Details from your registration. Contact admin to update these.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Company Info */}
                {fullProfile.company_trading_as && (
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <Building className="h-4 w-4" /> Company
                    </h4>
                    <InfoRow label="Trading As" value={fullProfile.company_trading_as} />
                  </div>
                )}
                
                {/* Bank Details */}
                {fullProfile.bank_name && (
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <CreditCard className="h-4 w-4" /> Bank Details
                    </h4>
                    <InfoRow label="Bank" value={fullProfile.bank_name} />
                    <InfoRow label="Account" value={fullProfile.account_number} />
                    <InfoRow label="Branch Code" value={fullProfile.branch_code} />
                    <InfoRow label="Type" value={fullProfile.account_type} />
                  </div>
                )}
                
                {/* Vehicle Info */}
                {fullProfile.vehicle_registration && (
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4" /> Vehicle
                    </h4>
                    <InfoRow label="Registration" value={fullProfile.vehicle_registration} />
                    <InfoRow label="Type" value={fullProfile.vehicle_type} />
                    <InfoRow label="Comments" value={fullProfile.vehicle_comments} />
                  </div>
                )}
                
                {/* License Info */}
                {fullProfile.driver_license && (
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-sm">
                      <FileText className="h-4 w-4" /> Documents
                    </h4>
                    <InfoRow label="License" value={fullProfile.driver_license} />
                    <InfoRow label="PDP" value={fullProfile.pdp} />
                    <InfoRow label="License Disc" value={fullProfile.license_disc} />
                  </div>
                )}
              </div>
              
              {/* Comments */}
              {(fullProfile.finance_comments || fullProfile.general_comments) && (
                <div className="mt-4 pt-4 border-t border-blue-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {fullProfile.finance_comments && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Finance Comments</p>
                        <p className="text-sm">{fullProfile.finance_comments}</p>
                      </div>
                    )}
                    {fullProfile.general_comments && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">General Comments</p>
                        <p className="text-sm">{fullProfile.general_comments}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Editable Profile Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
            <CardDescription>Update your contact details. Admin will be notified of changes.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleProfileUpdate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="full_name"
                      value={profileData.full_name}
                      onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                      className="pl-10"
                      data-testid="profile-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      value={user?.email || fullProfile?.email || ''}
                      disabled
                      className="pl-10 bg-muted"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="contact_number">Contact Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="contact_number"
                      value={profileData.contact_number}
                      onChange={(e) => setProfileData({ ...profileData, contact_number: e.target.value })}
                      placeholder="e.g., 082 123 4567"
                      className="pl-10"
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="address"
                      value={profileData.address}
                      onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                      placeholder="Your address"
                      className="pl-10"
                      data-testid="profile-address-input"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" disabled={profileLoading} data-testid="profile-save-button">
                  <Save className="h-4 w-4 mr-2" />
                  {profileLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Account Info */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Account Status</p>
                <p className={`font-medium ${(user?.is_active || fullProfile?.is_active) ? 'text-green-600' : 'text-red-600'}`}>
                  {(user?.is_active || fullProfile?.is_active) ? 'Active' : 'Inactive'}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Role</p>
                <p className="font-medium capitalize">{user?.role || fullProfile?.role}</p>
              </div>
              <div>
                <p className="text-muted-foreground">ID Number</p>
                <p className="font-medium">{user?.id_number || fullProfile?.id_number || 'Not set'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Member Since</p>
                <p className="font-medium">
                  {(user?.created_at || fullProfile?.created_at) 
                    ? new Date(user?.created_at || fullProfile?.created_at).toLocaleDateString() 
                    : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employees Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  My Drivers & Assistants
                </CardTitle>
                <CardDescription>
                  All drivers and assistants linked to your account. 
                  <span className="text-blue-600"> Blue badges = from registration data.</span>
                </CardDescription>
              </div>
              <Button onClick={() => { resetEmployeeForm(); setShowAddDialog(true); }} data-testid="add-employee-button">
                <UserPlus className="h-4 w-4 mr-2" />
                Add Employee
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {employeesLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : employees.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No employees yet</h3>
                <p className="text-muted-foreground mb-4">Add your drivers and assistants here</p>
                <Button onClick={() => { resetEmployeeForm(); setShowAddDialog(true); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Employee
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>ID Number</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>License/PDP</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.map((emp) => (
                      <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {emp.employee_type === 'driver' ? (
                              <><Car className="h-3 w-3 mr-1" />Driver</>
                            ) : (
                              <><User className="h-3 w-3 mr-1" />Assistant</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{emp.full_name || '-'}</TableCell>
                        <TableCell>{emp.id_number || '-'}</TableCell>
                        <TableCell>{emp.contact_number || '-'}</TableCell>
                        <TableCell>{emp.vehicle_registration || '-'}</TableCell>
                        <TableCell>
                          <div className="text-xs">
                            {emp.license_number && <div>L: {emp.license_number}</div>}
                            {emp.pdp_number && <div>PDP: {emp.pdp_number}</div>}
                            {!emp.license_number && !emp.pdp_number && '-'}
                          </div>
                        </TableCell>
                        <TableCell>{getStatusBadge(emp.status, emp.source)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {emp.source !== 'profile' && emp.source !== 'vehicle' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(emp)}
                                  disabled={emp.status === 'pending_deletion'}
                                  data-testid={`edit-employee-${emp.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteEmployee(emp)}
                                  disabled={emp.status === 'pending_deletion'}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-employee-${emp.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                            {(emp.source === 'profile' || emp.source === 'vehicle') && (
                              <span className="text-xs text-muted-foreground">Contact admin</span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            {employees.some(e => e.status === 'pending_deletion') && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Pending Deletions</p>
                  <p className="text-sm text-amber-700">Some employees are marked for deletion and awaiting admin approval.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  data-testid="password-current-input"
                />
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    required
                    data-testid="password-new-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    required
                    data-testid="password-confirm-input"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button type="submit" variant="outline" disabled={passwordLoading} data-testid="password-change-button">
                  <Lock className="h-4 w-4 mr-2" />
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Add Employee Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
              <DialogDescription>Add a driver or assistant to your profile. Admin will be notified.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddEmployee}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Employee Type *</Label>
                  <Select
                    value={employeeForm.employee_type}
                    onValueChange={(value) => setEmployeeForm({ ...employeeForm, employee_type: value })}
                  >
                    <SelectTrigger data-testid="employee-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="assistant">Assistant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emp_name">Full Name *</Label>
                    <Input
                      id="emp_name"
                      value={employeeForm.full_name}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, full_name: e.target.value })}
                      required
                      data-testid="employee-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp_id">ID Number *</Label>
                    <Input
                      id="emp_id"
                      value={employeeForm.id_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, id_number: e.target.value })}
                      required
                      data-testid="employee-id-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emp_contact">Contact Number *</Label>
                    <Input
                      id="emp_contact"
                      value={employeeForm.contact_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, contact_number: e.target.value })}
                      required
                      data-testid="employee-contact-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp_vehicle">Vehicle Registration</Label>
                    <Input
                      id="emp_vehicle"
                      value={employeeForm.vehicle_registration}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, vehicle_registration: e.target.value })}
                      placeholder="e.g., ABC 123 GP"
                      data-testid="employee-vehicle-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="emp_address">Address</Label>
                  <Input
                    id="emp_address"
                    value={employeeForm.address}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, address: e.target.value })}
                    data-testid="employee-address-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emp_license">License Number</Label>
                    <Input
                      id="emp_license"
                      value={employeeForm.license_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, license_number: e.target.value })}
                      data-testid="employee-license-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp_pdp">PDP Number</Label>
                    <Input
                      id="emp_pdp"
                      value={employeeForm.pdp_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, pdp_number: e.target.value })}
                      data-testid="employee-pdp-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="emp_notes">Notes</Label>
                  <Textarea
                    id="emp_notes"
                    value={employeeForm.notes}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={2}
                    data-testid="employee-notes-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={employeeLoading} data-testid="employee-submit-button">
                  {employeeLoading ? 'Adding...' : 'Add Employee'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Employee Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Employee</DialogTitle>
              <DialogDescription>Update employee details. Admin will be notified of changes.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleEditEmployee}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Employee Type</Label>
                  <Select
                    value={employeeForm.employee_type}
                    onValueChange={(value) => setEmployeeForm({ ...employeeForm, employee_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="driver">Driver</SelectItem>
                      <SelectItem value="assistant">Assistant</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_emp_name">Full Name</Label>
                    <Input
                      id="edit_emp_name"
                      value={employeeForm.full_name}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, full_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_emp_id">ID Number</Label>
                    <Input
                      id="edit_emp_id"
                      value={employeeForm.id_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, id_number: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_emp_contact">Contact Number</Label>
                    <Input
                      id="edit_emp_contact"
                      value={employeeForm.contact_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, contact_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_emp_vehicle">Vehicle Registration</Label>
                    <Input
                      id="edit_emp_vehicle"
                      value={employeeForm.vehicle_registration}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, vehicle_registration: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_emp_address">Address</Label>
                  <Input
                    id="edit_emp_address"
                    value={employeeForm.address}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, address: e.target.value })}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_emp_license">License Number</Label>
                    <Input
                      id="edit_emp_license"
                      value={employeeForm.license_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, license_number: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_emp_pdp">PDP Number</Label>
                    <Input
                      id="edit_emp_pdp"
                      value={employeeForm.pdp_number}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, pdp_number: e.target.value })}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_emp_notes">Notes</Label>
                  <Textarea
                    id="edit_emp_notes"
                    value={employeeForm.notes}
                    onChange={(e) => setEmployeeForm({ ...employeeForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={employeeLoading}>
                  {employeeLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
